import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

// Shared storage key with AuthContext's migration path: any teams saved
// locally while anonymous get migrated to Firestore on first sign-in.
const LOCAL_TEAMS_KEY = 'pokemon-champions-my-teams';

function readLocalTeams() {
  try {
    const raw = localStorage.getItem(LOCAL_TEAMS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeLocalTeams(list) {
  try {
    localStorage.setItem(LOCAL_TEAMS_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('Failed to persist local teams:', e);
  }
}

export function useTeams() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      // Anonymous: hydrate from localStorage and listen for cross-tab changes.
      const load = () => {
        const local = readLocalTeams()
          .slice()
          .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
        setTeams(local);
        setLoading(false);
      };
      load();
      const onStorage = (e) => { if (e.key === LOCAL_TEAMS_KEY) load(); };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    setLoading(true);
    const q = query(
      collection(db, 'users', user.uid, 'teams'),
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setTeams(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('teams snapshot error', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user]);

  const saveTeam = useCallback(async (team) => {
    const id = team.id || String(Date.now());
    const { id: _omit, ...data } = team;
    const payload = { ...data, updatedAt: new Date().toISOString() };
    if (!user) {
      // Anonymous: persist to localStorage under the same key the sign-in
      // migration picks up.
      const current = readLocalTeams();
      const existingIdx = current.findIndex((t) => String(t.id) === String(id));
      const next = existingIdx >= 0
        ? current.map((t, i) => i === existingIdx ? { id, ...payload } : t)
        : [{ id, ...payload }, ...current];
      writeLocalTeams(next);
      setTeams(next.slice().sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '')));
      return id;
    }
    const ref = doc(db, 'users', user.uid, 'teams', id);
    await setDoc(ref, payload);
    return id;
  }, [user]);

  const deleteTeam = useCallback(async (id) => {
    if (!user) {
      const next = readLocalTeams().filter((t) => String(t.id) !== String(id));
      writeLocalTeams(next);
      setTeams(next);
      return;
    }
    await deleteDoc(doc(db, 'users', user.uid, 'teams', id));
  }, [user]);

  return { teams, loading, saveTeam, deleteTeam };
}
